/**
 * Backend email OTP verification hook.
 *
 * Calls Django endpoints to send/confirm 6-digit codes via SES.
 * Persists successful verification to localStorage for 24 hours
 * so returning customers skip re-verification.
 */

import { useState, useCallback, useEffect } from 'react'
import { getEnvironmentConfig } from '@/config/environment'
import { auth } from '@/config/firebase'
import { saveToStorage, loadFromStorage, removeFromStorage, STORAGE_KEYS } from '@/utils/storage'

const VERIFICATION_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

export type EmailVerificationState =
  | 'idle'
  | 'sending'
  | 'code-sent'
  | 'verifying'
  | 'verified'
  | 'error'

interface PersistedVerification {
  email: string
  verifiedAt: number
  firebaseUid: string | null
}

interface UseEmailVerificationReturn {
  state: EmailVerificationState
  error: string | null
  codeSent: boolean
  attemptsRemaining: number | null
  expiresIn: number | null
  persistedEmail: string | null
  sendCode: (email: string) => Promise<void>
  verifyCode: (email: string, code: string) => Promise<void>
  reset: () => void
  clearPersistedVerification: () => void
}

function getFirebaseUid(): string | null {
  return auth.currentUser?.uid ?? null
}

function loadPersistedVerification(): PersistedVerification | null {
  const data = loadFromStorage<PersistedVerification>(STORAGE_KEYS.VERIFIED_EMAIL)
  if (!data) return null

  const age = Date.now() - data.verifiedAt
  if (age > VERIFICATION_TTL_MS) {
    removeFromStorage(STORAGE_KEYS.VERIFIED_EMAIL)
    return null
  }

  // If Firebase UID is available, verify it matches the persisted one
  const currentUid = getFirebaseUid()
  if (currentUid && data.firebaseUid && currentUid !== data.firebaseUid) {
    removeFromStorage(STORAGE_KEYS.VERIFIED_EMAIL)
    return null
  }

  return data
}

function persistVerification(email: string): void {
  const data: PersistedVerification = {
    email,
    verifiedAt: Date.now(),
    firebaseUid: getFirebaseUid(),
  }
  saveToStorage(STORAGE_KEYS.VERIFIED_EMAIL, data)
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const user = auth.currentUser
  if (user) {
    try {
      const token = await user.getIdToken()
      headers['Authorization'] = `Bearer ${token}`
    } catch {
      // Continue without auth — backend may allow unauthenticated verification
    }
  }
  const { restaurantId } = getEnvironmentConfig()
  if (restaurantId) headers['X-Restaurant-Id'] = restaurantId
  return headers
}

export function useEmailVerification(): UseEmailVerificationReturn {
  const [state, setState] = useState<EmailVerificationState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [codeSent, setCodeSent] = useState(false)
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null)
  const [expiresIn, setExpiresIn] = useState<number | null>(null)
  const [persistedEmail, setPersistedEmail] = useState<string | null>(null)

  const { apiBaseUrl } = getEnvironmentConfig()

  // On mount, check for a persisted (non-expired) verification
  useEffect(() => {
    const persisted = loadPersistedVerification()
    if (persisted) {
      setState('verified')
      setPersistedEmail(persisted.email)
    }
  }, [])

  const sendCode = useCallback(async (email: string) => {
    // If sending a code for a different email, clear the old verification
    const persisted = loadPersistedVerification()
    if (persisted && persisted.email !== email) {
      removeFromStorage(STORAGE_KEYS.VERIFIED_EMAIL)
      setPersistedEmail(null)
    }

    setState('sending')
    setError(null)

    try {
      const headers = await getAuthHeaders()
      const base = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`
      const res = await fetch(`${base}restohub-api/notifications/verify/send-code/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        if (res.status === 429) {
          throw new Error(data.error || 'Too many requests. Please try again later')
        }
        throw new Error(data.error || 'Failed to send verification code')
      }

      const data = await res.json()
      setExpiresIn(data.expires_in_seconds || 300)
      setCodeSent(true)
      setState('code-sent')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send code')
      setState('error')
    }
  }, [apiBaseUrl])

  const verifyCode = useCallback(async (email: string, code: string) => {
    setState('verifying')
    setError(null)

    try {
      const headers = await getAuthHeaders()
      const base = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`
      const res = await fetch(`${base}restohub-api/notifications/verify/confirm-code/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, code }),
      })

      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        if (res.status === 410) {
          throw new Error('Code expired. Please request a new one')
        }
        if (res.status === 429) {
          throw new Error('Too many attempts. Please request a new code')
        }
        if (data.attempts_remaining !== undefined) {
          setAttemptsRemaining(data.attempts_remaining)
        }
        throw new Error(data.error || 'Invalid verification code')
      }

      if (data.verified) {
        persistVerification(email)
        setPersistedEmail(email)
        setState('verified')
      } else {
        throw new Error('Verification failed')
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed')
      setState('error')
    }
  }, [apiBaseUrl])

  const clearPersistedVerification = useCallback(() => {
    removeFromStorage(STORAGE_KEYS.VERIFIED_EMAIL)
    setPersistedEmail(null)
    setState('idle')
    setError(null)
    setCodeSent(false)
    setAttemptsRemaining(null)
    setExpiresIn(null)
  }, [])

  const reset = useCallback(() => {
    setState('idle')
    setError(null)
    setCodeSent(false)
    setAttemptsRemaining(null)
    setExpiresIn(null)
  }, [])

  return {
    state, error, codeSent, attemptsRemaining, expiresIn,
    persistedEmail,
    sendCode, verifyCode, reset, clearPersistedVerification,
  }
}
